"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { getSession, clearSession } from "@/lib/auth";
import type { Session } from "@/lib/auth";
import { Menu, X, LogOut, LayoutDashboard, ChevronRight, User } from "lucide-react";
import { FixerIcon } from "@/components/layout/FixerIcon";

const navLinks = [
  { href: "/booking", label: "Get a Quote" },
  { href: "/pricing", label: "Pricing" },
  { href: "/contractor/onboarding", label: "For Contractors" },
  { href: "/blogs", label: "Blogs" },
];

export function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const [session, setSession] = useState<Session | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setSession(getSession());
    setIsMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    clearSession();
    setSession(null);
    router.push("/");
  };

  const isContractor = session?.role === "contractor";
  const isCustomer = session?.role === "customer";
  const isAdmin = session?.role === "admin";
  const isAdminPage = pathname.startsWith("/admin");

  // Admin portal & specialized dashboard exclusion cases
  if (pathname.startsWith("/admin") || pathname.startsWith("/contractor/dashboard")) return null;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 border-b border-slate-200/70 bg-white/95 backdrop-blur-xl transition-all duration-300 ${
        scrolled || isAdminPage ? "py-3 shadow-sm" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="brand-logo-shell group">
          <img src="/lastest-logo-fixer.png" alt="FixerLocal" className="brand-logo select-none transition-transform group-hover:scale-[1.03]" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className={`text-sm font-medium transition-colors ${
                pathname === link.href ? "text-emerald-600" : "text-slate-600 hover:text-emerald-600"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-4">
          {session ? (
            <div className="flex items-center gap-3">
              {!isAdmin ? (
                <>
                  <span className="text-xs font-medium text-slate-400 px-3 py-1 bg-slate-50 rounded-full border border-slate-100 italic">
                    {isContractor ? `Pro: ${session.contractorName}` : `Hi, ${session.user?.name?.split(" ")[0] || ""}`}
                  </span>
                  <Link
                    href={isContractor ? "/contractor/dashboard" : "/dashboard"}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold transition-all shadow-md shadow-emerald-100"
                  >
                    <LayoutDashboard size={14} />
                    Dashboard
                  </Link>
                </>
              ) : null}
              <button
                onClick={handleLogout}
                className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <>
              <Link href="/auth-choice" className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2 transition-colors">
                Log in
              </Link>
              <Link href="/auth-choice" className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2 transition-colors">
                Sign up
              </Link>
              <Link
                href="/booking"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold transition-all shadow-lg shadow-emerald-100 active:scale-[0.98]"
              >
                Get Started
                <ChevronRight size={14} />
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 text-slate-600"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 border-b border-slate-200 bg-white animate-slide-down p-6 flex flex-col gap-4 shadow-xl">
          {navLinks.map((link) => (
            <Link key={link.label} href={link.href} onClick={() => setIsMenuOpen(false)} className="text-base font-medium text-slate-900">
              {link.label}
            </Link>
          ))}
          <hr className="border-slate-100" />
          {session ? (
            <>
              {!isAdmin && (
                <Link href={isContractor ? "/contractor/dashboard" : "/dashboard"} onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2 text-base font-semibold text-slate-900">
                  <LayoutDashboard size={18} />
                  Dashboard
                </Link>
              )}
              <button onClick={handleLogout} className="flex items-center gap-2 text-base font-semibold text-red-600">
                <LogOut size={18} />
                Log out
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-3">
              <Link href="/auth-choice" onClick={() => setIsMenuOpen(false)} className="w-full py-3 rounded-xl border border-slate-200 text-center font-semibold text-slate-900">
                Log in
              </Link>
              <Link href="/auth-choice" onClick={() => setIsMenuOpen(false)} className="w-full py-3 rounded-xl border border-slate-200 text-center font-semibold text-slate-900">
                Sign up
              </Link>
              <Link href="/booking" onClick={() => setIsMenuOpen(false)} className="w-full py-3 rounded-xl bg-emerald-600 text-white text-center font-semibold">
                Get Started
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
